import type { EarlyAdopterPlanId } from '~/constants/plans.types';
import type { InviteeDiscount, User } from '~/types/user';

import { planIds } from '~/constants/plans';

type CommissionDeviationReasons =
  'loyalUser' | 'earlyAdopter' | 'freeTrial' | 'referralDiscount' | 'yearlyCopyrightLimitExceeded' | 'adminOverride';

const getCommissionDeviationReasons = (user: User): Set<CommissionDeviationReasons> => {
  const commissionDeviationReasons = new Set<CommissionDeviationReasons>();

  if (user.commissionSettings.isLoyalUser) commissionDeviationReasons.add('loyalUser');
  if (user.planId === planIds.earlyAdopter) commissionDeviationReasons.add('earlyAdopter');
  if (user.inviteeDiscount?.type === 'ShelterForFree') commissionDeviationReasons.add('freeTrial');
  if (user.invitorDiscounts.filter(({ isActive }) => isActive).length > 0) commissionDeviationReasons.add('referralDiscount');
  if (user.yearlyCopyrightLimitExceeded) commissionDeviationReasons.add('yearlyCopyrightLimitExceeded');
  if (user.commissionSettings.adminOverride) commissionDeviationReasons.add('adminOverride');

  // Remove certain deviation reasons to prevent redundant or illogical combinations

  if (commissionDeviationReasons.has('earlyAdopter')) {
    commissionDeviationReasons.delete('loyalUser');
  }

  if (commissionDeviationReasons.has('freeTrial')) {
    commissionDeviationReasons.delete('referralDiscount');
    commissionDeviationReasons.delete('yearlyCopyrightLimitExceeded');
    commissionDeviationReasons.delete('adminOverride');
  }

  if (commissionDeviationReasons.has('yearlyCopyrightLimitExceeded')) {
    commissionDeviationReasons.delete('referralDiscount');
    commissionDeviationReasons.delete('adminOverride');
  }

  return commissionDeviationReasons;
};

export const showLoyalUserTag = (user: User) => getCommissionDeviationReasons(user).has('loyalUser');

export const showEarlyAdopterTag = (user: User): user is User & { planId: EarlyAdopterPlanId } =>
  getCommissionDeviationReasons(user).has('earlyAdopter');

export const showFreeTrialTag = (user: User): user is User & { inviteeDiscount: InviteeDiscount & { type: 'ShelterForFree' } } =>
  getCommissionDeviationReasons(user).has('freeTrial');

export const showReferralDiscountTag = (user: User) => getCommissionDeviationReasons(user).has('referralDiscount');

export const showYearlyCopyrightLimitExceededTag = (user: User) => getCommissionDeviationReasons(user).has('yearlyCopyrightLimitExceeded');

export const showAdminOverrideTag = (user: User) => getCommissionDeviationReasons(user).has('adminOverride');
